import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Domicilios.css";

const Domicilios = () => {
    const [domicilios, setDomicilios] = useState([]);
    const [filtroFecha, setFiltroFecha] = useState("Todos");
    const [fechaInicio, setFechaInicio] = useState("");
    const [fechaFin, setFechaFin] = useState("");
    const [filtroBusqueda, setFiltroBusqueda] = useState("");
    const [editandoId, setEditandoId] = useState(null);
    const [domiciliarioEditado, setDomiciliarioEditado] = useState("");
    const [estadoEditado, setEstadoEditado] = useState("");
    const [precioDomicilioEditado, setPrecioDomicilioEditado] = useState(0);
    const [pagadoEditado, setPagadoEditado] = useState(false);

    // Obtener domicilios al cargar el componente
    useEffect(() => {
        fetchDomicilios();
    }, []);

    const fetchDomicilios = async () => {
        try {
            const response = await axios.get("https://backend-vbfh.onrender.com/api/domicilios");
            console.log(response.data); // Depuración: Verifica si numeroRemision está presente
            setDomicilios(response.data);
        } catch (error) {
            console.error("Error al obtener domicilios:", error);
        }
    };

    // Función para formatear precios
    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
        }).format(price);
    };

    // Función para manejar la edición de un domicilio
    const handleEditar = (domicilio) => {
        setEditandoId(domicilio.id);
        setDomiciliarioEditado(domicilio.domiciliario);
        setEstadoEditado(domicilio.estado);
        setPrecioDomicilioEditado(Number(domicilio.precio_domicilio) || 0);
        setPagadoEditado(domicilio.pagado || false);
    };

    // Función para guardar los cambios editados
    const handleGuardarCambios = async (id) => {
        try {
            const domicilioActual = domicilios.find((dom) => dom.id === id);
            const response = await axios.put(`https://backend-vbfh.onrender.com/api/domicilios/${id}`, {
                cliente: domicilioActual.cliente,
                direccion: domicilioActual.direccion,
                domiciliario: domiciliarioEditado,
                estado: estadoEditado,
                pagado: pagadoEditado,
                fecha: domicilioActual.fecha,
                precio_domicilio: precioDomicilioEditado,
            });

            if (response.status === 200) {
                const domiciliosActualizados = domicilios.map((domicilio) =>
                    domicilio.id === id
                        ? { ...domicilio, domiciliario: domiciliarioEditado, estado: estadoEditado, precio_domicilio: precioDomicilioEditado, pagado: pagadoEditado }
                        : domicilio
                );
                setDomicilios(domiciliosActualizados);
                setEditandoId(null);
                alert("Cambios guardados correctamente.");
            } else {
                alert("Error al guardar los cambios.");
            }
        } catch (error) {
            console.error("Error al guardar cambios:", error);
            alert("Hubo un error al guardar los cambios. Por favor, inténtalo de nuevo.");
        }
    };

    // Función para cancelar la edición
    const handleCancelarEdicion = () => {
        setEditandoId(null);
    };

    // Función para filtrar domicilios
    const filteredDomicilios = domicilios.filter((domicilio) => {
        const fechaDomicilio = new Date(domicilio.fecha);
        const hoy = new Date();
        const mesActual = hoy.getMonth();
        const añoActual = hoy.getFullYear();

        // Filtro por fecha
        let cumpleFecha = true;
        if (filtroFecha === "Hoy") {
            const mismoDia = fechaDomicilio.getDate() === hoy.getDate();
            const mismoMes = fechaDomicilio.getMonth() === hoy.getMonth();
            const mismoAño = fechaDomicilio.getFullYear() === hoy.getFullYear();
            cumpleFecha = mismoDia && mismoMes && mismoAño;
        } else if (filtroFecha === "Este mes") {
            cumpleFecha = fechaDomicilio.getMonth() === mesActual && fechaDomicilio.getFullYear() === añoActual;
        } else if (filtroFecha === "Rango") {
            const fechaInicioObj = new Date(fechaInicio);
            const fechaFinObj = new Date(fechaFin);
            cumpleFecha = fechaDomicilio >= fechaInicioObj && fechaDomicilio <= fechaFinObj;
        }

        // Filtro por búsqueda (domiciliario o número de remisión)
        const cumpleBusqueda =
            filtroBusqueda === "" ||
            domicilio.domiciliario.toLowerCase().includes(filtroBusqueda.toLowerCase()) ||
            domicilio.numeroremision.toString().includes(filtroBusqueda);

        return cumpleFecha && cumpleBusqueda;
    });

    // Ordenar domicilios por fecha (de la más reciente a la más antigua)
    const domiciliosOrdenados = filteredDomicilios.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

    // Calcular totales
    const totalPagado = domiciliosOrdenados
        .filter((domicilio) => domicilio.pagado)
        .reduce((total, domicilio) => total + (Number(domicilio.precio_domicilio) || 0), 0);

    const totalSinPagar = domiciliosOrdenados
        .filter((domicilio) => !domicilio.pagado)
        .reduce((total, domicilio) => total + (Number(domicilio.precio_domicilio) || 0), 0);

    // Función para formatear la fecha
    const formatFecha = (fecha) => {
        const fechaObj = new Date(fecha);
        return fechaObj.toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    return (
        <div className="domicilios-container">
            <h1>Gestión de Domicilios</h1>

            {/* Filtros */}
            <div className="filter-container">
                <label>Filtrar por fecha: </label>
                <select value={filtroFecha} onChange={(e) => setFiltroFecha(e.target.value)}>
                    <option value="Todos">Todos</option>
                    <option value="Hoy">Hoy</option>
                    <option value="Este mes">Este mes</option>
                    <option value="Rango">Rango de fechas</option>
                </select>

                {filtroFecha === "Rango" && (
                    <div className="rango-fechas">
                        <label>Desde:</label>
                        <input
                            type="date"
                            value={fechaInicio}
                            onChange={(e) => setFechaInicio(e.target.value)}
                        />
                        <label>Hasta:</label>
                        <input
                            type="date"
                            value={fechaFin}
                            onChange={(e) => setFechaFin(e.target.value)}
                        />
                    </div>
                )}

                <label>Buscar (domiciliario o remisión): </label>
                <input
                    type="text"
                    placeholder="Nombre del domiciliario o número de remisión"
                    value={filtroBusqueda}
                    onChange={(e) => setFiltroBusqueda(e.target.value)}
                />
            </div>

            {/* Resumen de totales */}
            <div className="resumen-totales">
                <p>Total Pagado: {formatPrice(totalPagado)}</p>
                <p>Total Sin Pagar: {formatPrice(totalSinPagar)}</p>
            </div>

            {/* Tabla de domicilios */}
            <table className="domicilios-table">
                <thead>
                    <tr>
                        <th>Cliente</th>
                        <th>Dirección</th>
                        <th>Domiciliario</th>
                        <th>Estado</th>
                        <th>Precio Domicilio</th>
                        <th>Pagado</th>
                        <th>N° Remisión</th>
                        <th>Fecha</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {domiciliosOrdenados.map((domicilio) => (
                        <tr key={domicilio.id}>
                            <td>{domicilio.cliente}</td>
                            <td>{domicilio.direccion}</td>
                            <td>
                                {editandoId === domicilio.id ? (
                                    <input
                                        type="text"
                                        value={domiciliarioEditado}
                                        onChange={(e) => setDomiciliarioEditado(e.target.value)}
                                    />
                                ) : (
                                    domicilio.domiciliario
                                )}
                            </td>
                            <td>
                                {editandoId === domicilio.id ? (
                                    <select
                                        value={estadoEditado}
                                        onChange={(e) => setEstadoEditado(e.target.value)}
                                    >
                                        <option value="Pendiente">Pendiente</option>
                                        <option value="En reparto">En reparto</option>
                                        <option value="Listo para enviar">Listo para enviar</option>
                                        <option value="Entregado">Entregado</option>
                                    </select>
                                ) : (
                                    domicilio.estado
                                )}
                            </td>
                            <td>
                                {editandoId === domicilio.id ? (
                                    <input
                                        type="number"
                                        value={precioDomicilioEditado}
                                        onChange={(e) => setPrecioDomicilioEditado(parseFloat(e.target.value))}
                                    />
                                ) : (
                                    formatPrice(Number(domicilio.precio_domicilio) || 0)
                                )}
                            </td>
                            <td>
                                {editandoId === domicilio.id ? (
                                    <input
                                        type="checkbox"
                                        checked={pagadoEditado}
                                        onChange={(e) => setPagadoEditado(e.target.checked)}
                                    />
                                ) : (
                                    domicilio.pagado ? "Sí" : "No"
                                )}
                            </td>
                            <td>{domicilio.numeroremision || "N/A"}</td>
                            <td>{formatFecha(domicilio.fecha)}</td>
                            <td>
                                {editandoId === domicilio.id ? (
                                    <>
                                        <button onClick={() => handleGuardarCambios(domicilio.id)}>Guardar</button>
                                        <button onClick={handleCancelarEdicion}>Cancelar</button>
                                    </>
                                ) : (
                                    <button onClick={() => handleEditar(domicilio)}>Editar</button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default Domicilios;